/**
 * UploadContacts — /#/upload-contacts
 *
 * Curated contact upload. Every contact lands in either:
 *   - active  (cleared at least one BANT letter — cadence-eligible)
 *   - holding (zero BANT signals — held up to 14 days for enrichment)
 *
 * Steps:
 *   1. Drop file (CSV / XLSX) or download template
 *   2. Map columns (auto-detected, user can correct)
 *   3. Validate + classify rows (active vs holding)
 *   4. Preview + confirm
 *   5. Insert tenant-scoped accounts + contacts via Supabase
 *
 * BANT gate logic lives in client/src/lib/bant.ts (single source of truth).
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import OperatorShell from "@/components/operator/OperatorShell";
import { useToast } from "@/hooks/use-toast";
import { getSession, getCurrentTenant, supabase } from "@/lib/session";
import {
  BANT_COLUMNS,
  OPTIONAL_COLUMNS,
  PLATFORM_COLUMNS,
  REQUIRED_COLUMNS,
  evaluateBant,
  lifecycleStateForLetters,
  validateRow,
  type ContactRow,
  type PlatformColumn,
} from "@/lib/bant";

// ── Brand tokens (match the rest of the app) ─────────────────────────────────
const BLACK = "#0B0B0F";
const RED = "#DC2626";
const SLATE_500 = "#6B6B70";

// ── Step type ────────────────────────────────────────────────────────────────
type Step = "drop" | "map" | "preview" | "done";

interface ParsedFile {
  filename: string;
  headers: string[];
  rows: Record<string, string>[];
}

interface ClassifiedRow {
  row: ContactRow;
  letters: ReturnType<typeof evaluateBant>;
  state: "active" | "holding";
  reasons: string[]; // shape errors (missing required, bad email)
}

// ── Header guess: lowercase + strip non-alpha ────────────────────────────────
function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

// ── Auto-map a source header to a platform column ────────────────────────────
const HEADER_HINTS: Record<PlatformColumn, string[]> = {
  first_name: ["firstname", "first", "givenname", "fname"],
  last_name: ["lastname", "last", "surname", "familyname", "lname"],
  email: ["email", "emailaddress", "mail"],
  company: ["company", "companyname", "account", "organization", "employer"],
  title: ["title", "jobtitle", "role", "position"],
  phone: ["phone", "phonenumber", "mobile", "cell"],
  linkedin_url: ["linkedin", "linkedinurl", "linkedinprofile", "li"],
  source: ["source", "leadsource", "channel"],
  notes: ["notes", "comments", "context"],
  budget_signal: ["budget", "budgetsignal", "budgetevidence"],
  authority_role: ["authority", "authorityrole", "decisionmaker"],
  need_evidence: ["need", "needevidence", "pain", "trigger"],
  timeline_signal: ["timeline", "timelinesignal", "timing", "decisiondate"],
  assigned_to: ["assignedto", "owner", "rep", "ae", "bdr"],
};

function guessMapping(headers: string[]): Record<PlatformColumn, string | ""> {
  const out: Record<string, string> = {};
  for (const col of PLATFORM_COLUMNS) {
    const hints = HEADER_HINTS[col];
    const match = headers.find((h) => {
      const n = normalize(h);
      return n === normalize(col) || hints.some((hint) => n === hint || n.includes(hint));
    });
    out[col] = match ?? "";
  }
  return out as Record<PlatformColumn, string | "">;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function UploadContacts() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const session = getSession();
  const tenant = getCurrentTenant();

  const [step, setStep] = useState<Step>("drop");
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [mapping, setMapping] = useState<Record<PlatformColumn, string | "">>(
    {} as Record<PlatformColumn, string | "">,
  );
  const [classified, setClassified] = useState<ClassifiedRow[] | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ active: number; holding: number; rejected: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Parse file ────────────────────────────────────────────────────────────
  const handleFile = useCallback(async (file: File) => {
    const lower = file.name.toLowerCase();
    try {
      if (lower.endsWith(".csv")) {
        const text = await file.text();
        const result = Papa.parse<Record<string, string>>(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h) => h.trim(),
        });
        const headers = (result.meta.fields ?? []).map((h) => h.trim()).filter(Boolean);
        const rows = (result.data ?? []).filter((r) =>
          Object.values(r).some((v) => (v ?? "").toString().trim()),
        );
        setParsed({ filename: file.name, headers, rows });
        setMapping(guessMapping(headers));
        setStep("map");
      } else if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: "" });
        const headers = Object.keys(json[0] ?? {});
        const rows = json.map((r) => {
          const out: Record<string, string> = {};
          for (const k of headers) out[k] = String(r[k] ?? "").trim();
          return out;
        });
        setParsed({ filename: file.name, headers, rows });
        setMapping(guessMapping(headers));
        setStep("map");
      } else {
        toast({ title: "Unsupported file", description: "Use .csv or .xlsx", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Could not read file", description: e?.message ?? String(e), variant: "destructive" });
    }
  }, [toast]);

  // ── Drag-and-drop handlers ────────────────────────────────────────────────
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  // ── Apply mapping → ContactRow[] ─────────────────────────────────────────
  const applyMapping = useCallback(() => {
    if (!parsed) return;
    const out: ClassifiedRow[] = parsed.rows.map((src) => {
      const row: Partial<ContactRow> = {};
      for (const col of PLATFORM_COLUMNS) {
        const sourceHeader = mapping[col];
        const v = sourceHeader ? (src[sourceHeader] ?? "").toString().trim() : "";
        if (v) (row as any)[col] = v;
      }
      const reasons = validateRow(row);
      const letters = evaluateBant(row);
      return {
        row: row as ContactRow,
        letters,
        state: lifecycleStateForLetters(letters),
        reasons,
      };
    });
    setClassified(out);
    setStep("preview");
  }, [parsed, mapping]);

  // ── Insert step ──────────────────────────────────────────────────────────
  const doImport = useCallback(async () => {
    if (!classified || !tenant || !session) return;
    setImporting(true);
    const sb = supabase();
    const valid = classified.filter((c) => c.reasons.length === 0);

    // 1. Resolve / create accounts per unique company name (tenant-scoped)
    const uniqueCompanies = Array.from(new Set(valid.map((c) => c.row.company.trim())));
    const accountIdByName = new Map<string, number>();

    try {
      // Look up existing accounts in tenant
      const { data: existing, error: existErr } = await sb
        .from("accounts")
        .select("id,name")
        .eq("tenant_id", tenant.id)
        .in("name", uniqueCompanies);
      if (existErr) throw existErr;
      for (const a of existing ?? []) accountIdByName.set(a.name, a.id);

      // Create the missing ones
      const missing = uniqueCompanies.filter((c) => !accountIdByName.has(c));
      if (missing.length > 0) {
        const { data: created, error: createErr } = await sb
          .from("accounts")
          .insert(
            missing.map((name) => ({
              tenant_id: tenant.id,
              owner_user_id: session.id,
              name,
              industry: "Other",
              // accounts.status is constrained — must be one of the AIP stage labels.
              status: "Suspect",
            })),
          )
          .select("id,name");
        if (createErr) throw createErr;
        for (const a of created ?? []) accountIdByName.set(a.name, a.id);
      }

      // 2. Insert contacts
      const nowIso = new Date().toISOString();
      const contactRows = valid.map(({ row, letters, state }) => ({
        tenant_id: tenant.id,
        account_id: accountIdByName.get(row.company.trim()) ?? null,
        created_by: session.id,
        assigned_to: session.id, // assigned_to text from CSV ignored for now — defaults to importer
        first_name: row.first_name,
        last_name: row.last_name,
        email: row.email.toLowerCase(),
        company: row.company,
        title: row.title,
        phone: row.phone ?? null,
        linkedin_url: row.linkedin_url ?? null,
        source: row.source ?? null,
        notes: row.notes ?? null,
        budget_signal: row.budget_signal ?? null,
        authority_role: row.authority_role ?? null,
        need_evidence: row.need_evidence ?? null,
        timeline_signal: row.timeline_signal ?? null,
        bant_letters_passed: letters,
        lifecycle_state: state,
        holding_started_at: state === "holding" ? nowIso : null,
        last_bant_check_at: nowIso,
      }));

      // Insert with onConflict so reruns don't crash on tenant+email unique
      const { error: insertErr, count } = await sb
        .from("contacts")
        .upsert(contactRows, { onConflict: "tenant_id,email", count: "exact" });
      if (insertErr) throw insertErr;

      const activeCount = valid.filter((v) => v.state === "active").length;
      const holdingCount = valid.filter((v) => v.state === "holding").length;
      const rejectedCount = classified.length - valid.length;
      setResult({ active: activeCount, holding: holdingCount, rejected: rejectedCount });
      setStep("done");
      toast({ title: "Import complete", description: `${count ?? valid.length} contacts processed.` });
    } catch (e: any) {
      toast({
        title: "Import failed",
        description: e?.message ?? String(e),
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  }, [classified, tenant, session, toast]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <OperatorShell breadcrumbs={[{ label: "Contacts" }, { label: "Upload" }]} pageTitle="Upload Contacts" pageMeta="Curated contact upload">
      <div className="max-w-4xl mx-auto">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5">
            Contacts · upload
          </div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: BLACK }}>
            Upload qualified contacts — not raw leads
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            Every contact you upload should already pass at least one qualifier letter — Budget,
            Authority, Need, or Timeline. Rows without a qualifier signal will enter Holding. They have 14
            days to gain a qualification signal before soft-kill.
          </p>
        </div>

        {/* ── Step rail ───────────────────────────────────────────────── */}
        <StepRail step={step} />

        {/* ── Step content ────────────────────────────────────────────── */}
        {step === "drop" && (
          <DropStep
            onPick={() => fileInputRef.current?.click()}
            onDrop={onDrop}
            dragOver={dragOver}
            setDragOver={setDragOver}
          />
        )}
        {step === "map" && parsed && (
          <MapStep
            parsed={parsed}
            mapping={mapping}
            setMapping={setMapping}
            onContinue={applyMapping}
            onBack={() => { setParsed(null); setStep("drop"); }}
          />
        )}
        {step === "preview" && classified && (
          <PreviewStep
            classified={classified}
            importing={importing}
            onImport={doImport}
            onBack={() => setStep("map")}
          />
        )}
        {step === "done" && result && (
          <DoneStep result={result} navigate={navigate} />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
      </div>
    </OperatorShell>
  );
}

// ── Step rail ────────────────────────────────────────────────────────────────

function StepRail({ step }: { step: Step }) {
  const items: { key: Step; label: string }[] = [
    { key: "drop", label: "1 · File" },
    { key: "map", label: "2 · Map columns" },
    { key: "preview", label: "3 · Preview" },
    { key: "done", label: "4 · Done" },
  ];
  const order: Step[] = ["drop", "map", "preview", "done"];
  const idx = order.indexOf(step);
  return (
    <div className="flex items-center gap-2 mb-6 text-[11px] uppercase tracking-[0.14em]">
      {items.map((it, i) => {
        const isActive = i === idx;
        const isDone = i < idx;
        return (
          <span
            key={it.key}
            className="px-3 py-1.5 rounded-full border"
            style={{
              borderColor: isActive ? BLACK : isDone ? "#C9C9CC" : "#E5E5E7",
              color: isActive ? BLACK : isDone ? "#525258" : "#8E8E94",
              background: isActive ? "white" : isDone ? "#EFEFEE" : "white",
              fontWeight: isActive ? 700 : 500,
            }}
          >
            {it.label}
          </span>
        );
      })}
    </div>
  );
}

// ── Step 1: Drop zone ────────────────────────────────────────────────────────

function DropStep(props: {
  onPick: () => void;
  onDrop: (e: React.DragEvent) => void;
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
}) {
  const { onPick, onDrop, dragOver, setDragOver } = props;
  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={onPick}
        className="rounded-2xl border-2 border-dashed cursor-pointer transition-colors"
        style={{
          borderColor: dragOver ? BLACK : "#C9C9CC",
          background: dragOver ? "#EFEFEE" : "white",
          padding: "56px 24px",
        }}
        data-testid="upload-dropzone"
      >
        <div className="text-center">
          <div
            className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4"
            style={{ background: "#EFEFEE" }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M12 16V4M12 4l-4 4m4-4l4 4M4 20h16" stroke={BLACK} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="text-base font-semibold" style={{ color: BLACK }}>
            Drag and drop a CSV or Excel file
          </div>
          <div className="text-sm text-neutral-500 mt-1">
            Or click to browse. .csv, .xlsx accepted.
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold" style={{ color: BLACK }}>Need a starting point?</div>
          <div className="text-xs text-neutral-500 mt-1">
            Our template lists every required and optional column with two example rows.
          </div>
        </div>
        <a
          href="/upload-template.csv"
          download="aip-upload-template.csv"
          className="text-xs px-3 py-2 rounded-md border whitespace-nowrap"
          style={{ borderColor: BLACK, color: BLACK }}
        >
          Download our upload template
        </a>
      </div>

      <RequiredColumnsCard />
    </div>
  );
}

function RequiredColumnsCard() {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5 text-sm">
      <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 mb-3">
        What the platform expects
      </div>
      <div className="grid sm:grid-cols-3 gap-4 text-xs">
        <div>
          <div className="font-semibold mb-1.5" style={{ color: BLACK }}>Required</div>
          <ul className="space-y-1 text-neutral-700">
            {REQUIRED_COLUMNS.map((c) => <li key={c}><code className="text-[11px]">{c}</code></li>)}
          </ul>
        </div>
        <div>
          <div className="font-semibold mb-1.5" style={{ color: BLACK }}>Qualifier signals (≥ 1 to be active)</div>
          <ul className="space-y-1 text-neutral-700">
            {BANT_COLUMNS.map((c) => <li key={c}><code className="text-[11px]">{c}</code></li>)}
          </ul>
        </div>
        <div>
          <div className="font-semibold mb-1.5" style={{ color: BLACK }}>Optional</div>
          <ul className="space-y-1 text-neutral-700">
            {OPTIONAL_COLUMNS.map((c) => <li key={c}><code className="text-[11px]">{c}</code></li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ── Step 2: Column mapping ───────────────────────────────────────────────────

function MapStep(props: {
  parsed: ParsedFile;
  mapping: Record<PlatformColumn, string | "">;
  setMapping: (m: Record<PlatformColumn, string | "">) => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  const { parsed, mapping, setMapping, onContinue, onBack } = props;
  const requiredOk = REQUIRED_COLUMNS.every((c) => mapping[c]);
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 mb-1">
          File · {parsed.filename}
        </div>
        <div className="text-sm text-neutral-700 mb-4">
          {parsed.rows.length} rows detected. Map your columns to the platform schema. Required
          columns must all be assigned.
        </div>
        <div className="space-y-2.5">
          {PLATFORM_COLUMNS.map((col) => {
            const isRequired = (REQUIRED_COLUMNS as readonly string[]).includes(col);
            const isBant = (BANT_COLUMNS as readonly string[]).includes(col);
            return (
              <div key={col} className="grid grid-cols-[1fr_auto_2fr] items-center gap-3">
                <div className="text-sm">
                  <code className="text-[12px]" style={{ color: BLACK }}>{col}</code>
                  {isRequired && <span className="ml-1.5 text-[10px] font-bold uppercase" style={{ color: RED }}>required</span>}
                  {isBant && <span className="ml-1.5 text-[10px] font-bold uppercase text-neutral-500">bant</span>}
                </div>
                <div className="text-neutral-300 text-xs">←</div>
                <select
                  value={mapping[col] ?? ""}
                  onChange={(e) => setMapping({ ...mapping, [col]: e.target.value })}
                  className="w-full text-sm rounded-md border border-neutral-200 px-2 py-1.5 bg-white"
                >
                  <option value="">— skip —</option>
                  {parsed.headers.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex justify-between items-center">
        <button
          onClick={onBack}
          className="text-xs px-3 py-2 rounded-md border border-neutral-200 text-neutral-600"
        >
          ← Pick a different file
        </button>
        <button
          onClick={onContinue}
          disabled={!requiredOk}
          className="text-xs px-4 py-2 rounded-md font-semibold uppercase tracking-wider text-white disabled:opacity-40"
          style={{ background: BLACK }}
        >
          Continue to preview →
        </button>
      </div>
      {!requiredOk && (
        <div className="text-xs" style={{ color: RED }}>
          Map all required columns to continue.
        </div>
      )}
    </div>
  );
}

// ── Step 3: Preview ──────────────────────────────────────────────────────────

function PreviewStep(props: {
  classified: ClassifiedRow[];
  importing: boolean;
  onImport: () => void;
  onBack: () => void;
}) {
  const { classified, importing, onImport, onBack } = props;
  const valid = classified.filter((c) => c.reasons.length === 0);
  const rejected = classified.filter((c) => c.reasons.length > 0);
  const active = valid.filter((c) => c.state === "active");
  const holding = valid.filter((c) => c.state === "holding");
  const sample = valid.slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <CountCard
          label="Active import"
          value={active.length}
          tone="navy"
          sub="Cleared at least one qualifier letter — straight to active flow, cadence-eligible."
        />
        <CountCard
          label="Holding"
          value={holding.length}
          tone="amber"
          sub="Zero qualifier signals — held up to 14 days for enrichment, then soft-killed."
        />
        <CountCard
          label="Rejected"
          value={rejected.length}
          tone="red"
          sub="Missing required fields or invalid email. Fix the source file and re-upload."
        />
      </div>

      {sample.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
          <div className="px-5 py-3 text-[11px] uppercase tracking-[0.14em] text-neutral-500 border-b border-neutral-100">
            Preview · first {sample.length} valid rows
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Title</th>
                  <th className="px-4 py-2">Company</th>
                  <th className="px-4 py-2">Qualifier Letters</th>
                  <th className="px-4 py-2">Lifecycle</th>
                </tr>
              </thead>
              <tbody>
                {sample.map((c, i) => (
                  <tr key={i} className="border-b border-neutral-50 last:border-0">
                    <td className="px-4 py-2 font-medium" style={{ color: BLACK }}>
                      {c.row.first_name} {c.row.last_name}
                    </td>
                    <td className="px-4 py-2 text-neutral-600">{c.row.title}</td>
                    <td className="px-4 py-2 text-neutral-600">{c.row.company}</td>
                    <td className="px-4 py-2">
                      {c.letters.length === 0 ? (
                        <span className="text-[11px] text-neutral-500">—</span>
                      ) : (
                        <div className="flex gap-1">
                          {c.letters.map((l) => (
                            <span key={l} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#0B0B0F", color: "white" }}>
                              {l}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
                        style={c.state === "active"
                          ? { background: "#DCFCE7", color: "#166534" }
                          : { background: "#FEF3C7", color: "#92400E" }}
                      >
                        {c.state}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rejected.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-xs text-rose-900">
          <div className="font-semibold mb-2" style={{ color: RED }}>{rejected.length} rejected rows</div>
          <ul className="space-y-1">
            {rejected.slice(0, 5).map((r, i) => (
              <li key={i}>
                Row {classified.indexOf(r) + 1}: {r.row.email || r.row.first_name || "blank"} — {r.reasons.join(", ")}
              </li>
            ))}
            {rejected.length > 5 && <li className="text-rose-700">…and {rejected.length - 5} more.</li>}
          </ul>
        </div>
      )}

      <div className="flex justify-between items-center">
        <button
          onClick={onBack}
          className="text-xs px-3 py-2 rounded-md border border-neutral-200 text-neutral-600"
        >
          ← Back to mapping
        </button>
        <button
          onClick={onImport}
          disabled={importing || valid.length === 0}
          className="text-xs px-5 py-2.5 rounded-md font-semibold uppercase tracking-wider text-white disabled:opacity-40"
          style={{ background: RED }}
          data-testid="confirm-import-btn"
        >
          {importing ? "Importing..." : `Import ${active.length} active and ${holding.length} holding`}
        </button>
      </div>
    </div>
  );
}

function CountCard(props: { label: string; value: number; tone: "navy" | "amber" | "red"; sub: string }) {
  const palette = {
    navy: { bg: "white", border: "#0B0B0F", num: "#0B0B0F" },
    amber: { bg: "white", border: "#EAB308", num: "#92400E" },
    red: { bg: "white", border: "#FCA5A5", num: "#B91C1C" },
  }[props.tone];
  return (
    <div className="rounded-xl border-2 p-4" style={{ background: palette.bg, borderColor: palette.border }}>
      <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: SLATE_500 }}>{props.label}</div>
      <div className="text-3xl font-bold tabular-nums mt-1" style={{ color: palette.num }}>{props.value}</div>
      <div className="text-[11px] text-neutral-500 mt-1.5 leading-relaxed">{props.sub}</div>
    </div>
  );
}

// ── Step 4: Done ─────────────────────────────────────────────────────────────

function DoneStep(props: {
  result: { active: number; holding: number; rejected: number };
  navigate: (path: string) => void;
}) {
  const { result, navigate } = props;
  const { active, holding, rejected } = result;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-neutral-200 bg-white p-8">
        <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-2">Import complete</div>
        <h2 className="text-xl font-semibold mb-3" style={{ color: BLACK }}>
          {active} active, {holding} in holding
        </h2>
        <p className="text-sm text-neutral-600 mb-4 max-w-xl">
          {active} contacts cleared CP1 with at least one qualifier letter and are cadence-eligible.
          {" "}{holding} entered the holding queue with 14 days to gain a qualification signal.
          {rejected > 0 ? ` ${rejected} rows were rejected for missing required fields.` : ""}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate(session?.role === "bdr" ? "/cadence/bdr" : "/cadence/ae")}
            className="text-xs px-4 py-2 rounded-md font-semibold uppercase tracking-wider text-white"
            style={{ background: RED }}
            data-testid="start-cadence-btn"
          >
            Start your cadence →
          </button>
          {holding > 0 && (
            <button
              onClick={() => navigate("/contacts/holding")}
              className="text-xs px-4 py-2 rounded-md border font-semibold uppercase tracking-wider"
              style={{ borderColor: BLACK, color: BLACK }}
            >
              Review holding queue
            </button>
          )}
          <button
            onClick={() => navigate("/accounts")}
            className="text-xs px-4 py-2 rounded-md border border-neutral-200 text-neutral-600"
          >
            Open accounts
          </button>
        </div>
      </div>
    </div>
  );
}
